import React, { useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import './Converter.css';

function ConverterList({ conversions, onViewConversion, onNewConversion }) {
  return (
    <div className="converter-container">
      <div className="converter-header">
        <div>
          <h1>Converter</h1>
          <p>Convert Tableau workbooks to Power BI</p>
        </div>
        <button className="primary-button" onClick={onNewConversion}>
          + New Conversion
        </button>
      </div>

      <div className="conversions-grid">
        {conversions.map((conversion) => (
          <div key={conversion.id} className="conversion-card">
            <div className="conversion-header">
              <h3>{conversion.name}</h3>
              <span className={`status-badge ${conversion.status.toLowerCase().replace(' ', '-')}`}>
                {conversion.status}
              </span>
            </div>
            <div className="conversion-details">
              <p><strong>File:</strong> {conversion.fileName}</p>
              <p><strong>Size:</strong> {conversion.size}</p>
              <p><strong>Date:</strong> {conversion.date}</p>
              {conversion.status === 'Completed' && (
                <p><strong>Success Rate:</strong> {conversion.successRate}%</p>
              )}
            </div>
            <button
              className="secondary-button"
              onClick={() => onViewConversion(conversion.id)}
            >
              View Details
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConversionDetail({ conversion, onBack }) {
  const handleDownload = () => {
    alert('Downloading Power BI file... In a real app, this would download the .pbip file');
  };

  return (
    <div className="converter-container">
      <div className="converter-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Conversions
        </button>
      </div>

      <div className="conversion-detail">
        <div className="detail-header">
          <h1>{conversion.name}</h1>
          <div className="header-actions">
            <span className={`status-badge ${conversion.status.toLowerCase().replace(' ', '-')}`}>
              {conversion.status}
            </span>
            {conversion.status === 'Completed' && (
              <button className="primary-button" onClick={handleDownload}>
                Download PBIP
              </button>
            )}
          </div>
        </div>

        <div className="detail-info">
          <div className="info-item">
            <label>Source File:</label>
            <span>{conversion.fileName}</span>
          </div>
          <div className="info-item">
            <label>Date:</label>
            <span>{conversion.date}</span>
          </div>
          <div className="info-item">
            <label>Size:</label>
            <span>{conversion.size}</span>
          </div>
        </div>

        {conversion.status === 'Completed' && (
          <>
            <div className="conversion-summary">
              <h2>Conversion Summary</h2>
              <div className="summary-grid">
                <div className="summary-box success">
                  <h3>{conversion.successRate}%</h3>
                  <p>Success Rate</p>
                </div>
                <div className="summary-box">
                  <h3>{conversion.visualsConverted}</h3>
                  <p>Visuals Converted</p>
                </div>
                <div className="summary-box">
                  <h3>{conversion.datasetsConverted}</h3>
                  <p>Datasets Converted</p>
                </div>
                <div className="summary-box">
                  <h3>{conversion.warnings}</h3>
                  <p>Warnings</p>
                </div>
              </div>
            </div>

            <div className="conversion-log">
              <h2>Conversion Log</h2>
              <div className="log-container">
                {conversion.log.map((entry, index) => (
                  <div key={index} className={`log-entry ${entry.type}`}>
                    <span className="log-timestamp">{entry.timestamp}</span>
                    <span className={`log-type ${entry.type}`}>{entry.type.toUpperCase()}</span>
                    <span className="log-message">{entry.message}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {conversion.status === 'In Progress' && (
          <div className="conversion-progress">
            <h2>Conversion in Progress</h2>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: '45%' }}></div>
            </div>
            <p>Processing visuals and datasets...</p>
          </div>
        )}

        {conversion.status === 'Failed' && (
          <div className="conversion-error">
            <h2>Conversion Failed</h2>
            <div className="error-message">
              <p>The conversion failed due to unsupported features in the source workbook.</p>
              <p>Please review the error log below for details.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NewConversion({ onCancel, onSubmit }) {
  const [file, setFile] = useState(null);
  const [conversionName, setConversionName] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (!conversionName) {
        setConversionName(selectedFile.name.replace('.twbx', ''));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ name: conversionName, file });
  };

  return (
    <div className="converter-container">
      <div className="converter-header">
        <button className="back-button" onClick={onCancel}>
          ← Cancel
        </button>
      </div>

      <div className="new-conversion-form">
        <h1>New Conversion</h1>
        <p>Upload a Tableau workbook file to convert to Power BI</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Conversion Name</label>
            <input
              type="text"
              value={conversionName}
              onChange={(e) => setConversionName(e.target.value)}
              placeholder="e.g., Sales Dashboard"
              required
            />
          </div>

          <div className="form-group">
            <label>Tableau Workbook File (.twbx)</label>
            <div className="file-upload-area">
              <input
                type="file"
                id="file-upload"
                accept=".twbx,.twb"
                onChange={handleFileChange}
                required
              />
              <label htmlFor="file-upload" className="file-upload-label">
                <span className="upload-icon">↥</span>
                <span>{file ? file.name : 'Click to select file or drag and drop'}</span>
                <span className="file-info">Supported formats: .twbx, .twb</span>
              </label>
            </div>
          </div>

          <div className="conversion-options">
            <h3>Conversion Options</h3>
            <div className="checkbox-group">
              <label>
                <input type="checkbox" defaultChecked />
                Preserve color schemes
              </label>
              <label>
                <input type="checkbox" defaultChecked />
                Convert calculated fields
              </label>
              <label>
                <input type="checkbox" defaultChecked />
                Maintain filter settings
              </label>
              <label>
                <input type="checkbox" />
                Generate migration report
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="secondary-button" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="primary-button">
              Start Conversion
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Converter() {
  const navigate = useNavigate();
  const [conversions] = useState([
    {
      id: 1,
      name: 'Sales Dashboard',
      fileName: 'Sales_Dashboard.twbx',
      size: '2.4 MB',
      status: 'Completed',
      date: '2025-12-22',
      successRate: 92,
      visualsConverted: 15,
      datasetsConverted: 3,
      warnings: 2,
      log: [
        { timestamp: '10:23:45', type: 'info', message: 'Starting conversion process' },
        { timestamp: '10:23:46', type: 'info', message: 'Extracting workbook metadata' },
        { timestamp: '10:23:48', type: 'info', message: 'Converting data sources (1/3)' },
        { timestamp: '10:23:50', type: 'warning', message: 'Custom SQL detected - manual review recommended' },
        { timestamp: '10:23:52', type: 'info', message: 'Converting visualizations (15/15)' },
        { timestamp: '10:23:55', type: 'warning', message: 'Table calculation converted with limitations' },
        { timestamp: '10:23:58', type: 'success', message: 'Conversion completed successfully' }
      ]
    },
    {
      id: 2,
      name: 'Finance Report',
      fileName: 'Finance_Report.twbx',
      size: '3.1 MB',
      status: 'In Progress',
      date: '2025-12-21',
      successRate: 0,
      visualsConverted: 0,
      datasetsConverted: 0,
      warnings: 0,
      log: []
    },
    {
      id: 3,
      name: 'HR Metrics',
      fileName: 'HR_Metrics.twbx',
      size: '1.8 MB',
      status: 'Completed',
      date: '2025-12-19',
      successRate: 100,
      visualsConverted: 8,
      datasetsConverted: 2,
      warnings: 0,
      log: [
        { timestamp: '14:10:22', type: 'info', message: 'Starting conversion process' },
        { timestamp: '14:10:23', type: 'info', message: 'Extracting workbook metadata' },
        { timestamp: '14:10:25', type: 'info', message: 'Converting data sources (2/2)' },
        { timestamp: '14:10:28', type: 'info', message: 'Converting visualizations (8/8)' },
        { timestamp: '14:10:30', type: 'success', message: 'Conversion completed successfully' }
      ]
    }
  ]);

  const [selectedConversion, setSelectedConversion] = useState(null);

  const handleViewConversion = (id) => {
    const conversion = conversions.find(c => c.id === id);
    setSelectedConversion(conversion);
    navigate('/converter/detail');
  };

  const handleNewConversion = () => {
    navigate('/converter/new');
  };

  const handleBack = () => {
    setSelectedConversion(null);
    navigate('/converter');
  };

  const handleSubmitConversion = (data) => {
    alert(`Conversion started for: ${data.name}\nIn a real app, the file would be uploaded and converted.`);
    navigate('/converter');
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <ConverterList
            conversions={conversions}
            onViewConversion={handleViewConversion}
            onNewConversion={handleNewConversion}
          />
        }
      />
      <Route
        path="/detail"
        element={
          selectedConversion ? (
            <ConversionDetail conversion={selectedConversion} onBack={handleBack} />
          ) : (
            <ConverterList
              conversions={conversions}
              onViewConversion={handleViewConversion}
              onNewConversion={handleNewConversion}
            />
          )
        }
      />
      <Route
        path="/new"
        element={
          <NewConversion onCancel={handleBack} onSubmit={handleSubmitConversion} />
        }
      />
    </Routes>
  );
}

export default Converter;
